<template>
  <div>
    <div :class="$style.formGroup">
      <label for="shopSectionName" :class="$style.label">StoreItem name *</label>
      <InputText
        id="shopSectionName"
        v-model="shopItemName"
        :class="{ [$style.pInvalid]: shopItemName && isDuplicate }"
        placeholder="Name"
        autofocus
        @input="validate"
        @keydown.enter="save"
      />
      <small :class="[$style.info, $style.pError]">{{ isDuplicate ? "StoreItem name already exists" : "" }}</small>
      <small :class="[$style.info, $style.pError]">
        {{ !isShopnameValid && shopItemName.length > 0 ? "StoreItem name should not exceed 100 characters" : "" }}
      </small>
    </div>
    <div :class="[$style.formGroup, $style.rewardFormGroup]">
      <label :class="$style.label">Base reward *</label>
      <RewardDropdown
        :model-value="selectedReward"
        :game-id="gameId"
        :version-id="versionId"
        @select-reward="selectReward"
      />
    </div>
    <div :class="$style.formGroup">
      <label :class="$style.label">Item tags</label>
      <TagsManager v-model="selectedTags" :game-id="gameId" :version-id="versionId" has-borders :type="'SHOP_ITEM'" />
    </div>
    <div :class="$style.formGroup">
      <label :class="$style.label">Set price *</label>
      <ShopItemPriceForm
        :game-id="gameId"
        :version-id="versionId"
        :price="price"
        :selected-price-type="selectedPriceType || ''"
        :disabled="selectedReward === null || nonConsumableIds.length > 0"
        :non-consumable-ids="nonConsumableIds"
        @price-type-update="handlePriceTypeUpdate"
        @price-update="handlePriceUpdate"
      />
      <small v-if="!isPriceValid.status" :class="[$style.info, $style.pError]">
        {{ isPriceValid.message }}
      </small>
    </div>
    <div v-if="isShopItemConditionsEnabled">
      <h2>Conditions</h2>
      <div :class="$style.formGroupRow">
        <Checkbox v-model="isPurchaseLimitActive" :binary="true"></Checkbox>
        <label>Add purchase limit</label>
      </div>
      <div v-if="isPurchaseLimitActive" :class="[$style.formGroup, $style.paddingLeft]">
        <label :class="$style.label">Purchase limit</label>
        <InputNumber
          v-model="purchaseLimit"
          :min="1"
          :max-fraction-digits="0"
          :min-fraction-digits="0"
          :max="99999999"
        />
      </div>
      <div :class="$style.formGroupRow">
        <Checkbox v-model="isPurchaseCooldownActive" :binary="true"></Checkbox>
        <label>Add purchase cooldown</label>
      </div>
      <div v-if="isPurchaseCooldownActive" :class="[$style.formGroupRow, $style.paddingLeft]">
        <div :class="$style.formGroup">
          <label :class="$style.label">Days</label>
          <InputNumber
            v-model="purchaseCooldownDays"
            :min="1"
            :max-fraction-digits="0"
            :min-fraction-digits="0"
            :max="99999999"
          />
        </div>
        <div :class="$style.formGroup">
          <label :class="$style.label">Hours</label>
          <InputNumber
            v-model="purchaseCooldownHours"
            :min="1"
            :max-fraction-digits="0"
            :min-fraction-digits="0"
            :max="23"
          />
        </div>
      </div>
    </div>
    <p :class="[$style.required, $style.pValid]">* = required</p>
    <div :class="$style.actions">
      <Button label="Cancel" class="p-button-primary" outlined @click="cancel" />
      <Button
        label="Save"
        class="p-button-primary"
        :disabled="!selectedReward || !shopItemName || isDuplicate || !isShopnameValid || !isPriceValid.status"
        @click="save"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { SHOP_ITEM_CONDITIONS_ALLOWED_APP_IDS } from "@/constants";
import type { ShopItemPriceInput, ShopItem as ShopItemType } from "@/graphql";
import { PriceInput, PriceInputType, Reward, ShopSection } from "../shop.types";
import RewardDropdown from "./RewardDropdown.vue";
import ShopItemPriceForm from "./ShopItemPriceForm.vue";

const emit = defineEmits(["update-shop-item", "save-shop-item", "close"]);
const props = defineProps<{
  section: ShopSection;
  shopItemToBeEdited: ShopItemType | null;
}>();

const route = useRoute();

const gameId = route.params.gameId as string;
const versionId = route.params.versionId as string;

const isShopItemConditionsEnabled = computed(() => SHOP_ITEM_CONDITIONS_ALLOWED_APP_IDS.includes(gameId));

const isValid = ref(false);
const isShopnameValid = ref(false);
const isDuplicate = ref(false);
const isPriceValid = ref({
  status: false,
  message: "",
});

const shopItemName = ref("");
const selectedTags = ref<{ id: string; name: string }[]>([]);
const selectedReward = ref<Reward | null>(null);
const selectedPriceType = ref<string | null>(null);
const price = ref<PriceInput>({
  priceType: null,
  IAP: {
    ios: {
      id: "",
    },
    android: {
      id: "",
    },
  },
  inGameCurrency: {
    itemId: "",
    quantity: 0,
  },
  RV: {
    quantity: 1,
  },
});
const nonConsumableIds = ref<string[]>([]);

const isPurchaseLimitActive = ref(props.shopItemToBeEdited ? props.shopItemToBeEdited?.purchaseLimit !== -1 : false);
const isPurchaseCooldownActive = ref(
  props.shopItemToBeEdited
    ? props.shopItemToBeEdited?.coolDown.hours !== 0 || props.shopItemToBeEdited?.coolDown.days !== 0
    : false
);
const purchaseLimit = ref(-1);
const purchaseCooldownDays = ref(0);
const purchaseCooldownHours = ref(0);

watch(
  () => props.shopItemToBeEdited,
  (newVal) => {
    isPurchaseLimitActive.value = !!(newVal && newVal.purchaseLimit !== -1);
  },
  {
    immediate: true,
  }
);

watch(
  () => isPurchaseLimitActive.value,
  (newVal) => {
    if (newVal) {
      purchaseLimit.value =
        props.shopItemToBeEdited?.purchaseLimit === -1 ? 1 : props.shopItemToBeEdited?.purchaseLimit ?? 1;
    } else {
      purchaseLimit.value = -1;
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => isPurchaseCooldownActive.value,
  (newVal) => {
    if (newVal) {
      purchaseCooldownDays.value = props.shopItemToBeEdited?.coolDown.days ?? 0;
      purchaseCooldownHours.value = props.shopItemToBeEdited?.coolDown.hours ?? 0;
    } else {
      purchaseCooldownDays.value = 0;
      purchaseCooldownHours.value = 0;
    }
  },
  {
    immediate: true,
  }
);

const selectReward = (reward: Reward | null) => {
  selectedReward.value = reward;

  if (reward?.items.some((item) => item.type === "NON_CONSUMABLE")) {
    selectedPriceType.value = "IAP";
    price.value.priceType = "IAP";
    nonConsumableIds.value = reward.items.filter((item) => item.type === "NON_CONSUMABLE").map((item) => item.id);
  } else {
    nonConsumableIds.value = [];
  }
};

const cancel = () => {
  shopItemName.value = "";
  isValid.value = false;
  isDuplicate.value = false;
  selectedTags.value = [];
  selectedReward.value = null;
  purchaseLimit.value = -1;
  isPurchaseLimitActive.value = false;
  emit("close");
};

const validatePrice = () => {
  if (!price.value) {
    isPriceValid.value = {
      status: false,
      message: "Price is required",
    };
    return;
  }
  if (price.value.priceType === "IAP") {
    if (!price.value.IAP.ios.id || !price.value.IAP.android.id) {
      isPriceValid.value = {
        status: false,
        message: "Please select both iOS and Android IAPs",
      };
      return;
    }
  }
  if (price.value.priceType === "IN_GAME_CURRENCY") {
    if (!price.value.inGameCurrency.itemId || price.value.inGameCurrency.quantity === 0) {
      isPriceValid.value = {
        status: false,
        message: "Please select an item and quantity",
      };
      return;
    }
  }
  if (price.value.priceType === "RV") {
    if (price.value.RV.quantity === 0) {
      isPriceValid.value = {
        status: false,
        message: "Quantity should be positive and non null",
      };
      return;
    }
  }
  isPriceValid.value.status = true;
};

const validate = () => {
  shopItemName.value = shopItemName.value.trim();
  isShopnameValid.value = shopItemName.value.length > 0 && shopItemName.value.length <= 100;
  isValid.value = isShopnameValid.value && !!selectedReward.value;
  isDuplicate.value = props.section.items.some(
    (item) =>
      item.name === shopItemName.value && (!props.shopItemToBeEdited || item.id !== props.shopItemToBeEdited?.id)
  );
};

const resetPricing = () => {
  price.value.IAP.ios.id = "";
  price.value.IAP.android.id = "";
  price.value.inGameCurrency.itemId = "";
  price.value.inGameCurrency.quantity = 0;
  price.value.RV.quantity = 1;
};

const save = () => {
  validate();
  validatePrice();
  if (isValid.value && !isDuplicate.value && isPriceValid.value.status) {
    const formattedTags = selectedTags.value.map((tag) => tag.id);

    const priceFormated: ShopItemPriceInput = {
      priceType: price.value.priceType || "IAP",
      ...(price.value.priceType === "IAP" && {
        IAP: {
          ios: {
            id: price.value.IAP.ios.id,
          },
          android: {
            id: price.value.IAP.android.id,
          },
        },
      }),
      ...(price.value.priceType === "IN_GAME_CURRENCY" && {
        inGameCurrency: {
          itemId: price.value.inGameCurrency.itemId,
          quantity: price.value.inGameCurrency.quantity,
        },
      }),
      ...(price.value.priceType === "RV" && {
        RV: {
          quantity: price.value.RV.quantity,
        },
      }),
    };

    const eventName = props.shopItemToBeEdited ? "update-shop-item" : "save-shop-item";

    emit(eventName, {
      name: shopItemName.value,
      tags: formattedTags,
      rewardId: selectedReward.value?.id,
      price: priceFormated,
      purchaseLimit: purchaseLimit.value,
      coolDown: {
        days: purchaseCooldownDays.value,
        hours: purchaseCooldownHours.value,
      },
    });

    shopItemName.value = "";
    isValid.value = false;
    isDuplicate.value = false;
    selectedTags.value = [];
    selectedReward.value = null;
    isPurchaseLimitActive.value = false;
    purchaseLimit.value = -1;
    isPurchaseCooldownActive.value = false;
    purchaseCooldownDays.value = 0;
    purchaseCooldownHours.value = 0;
    resetPricing();
  }
};

const handlePriceTypeUpdate = (priceType: PriceInputType) => {
  if (price.value.priceType !== priceType) {
    price.value.priceType = priceType;
    selectedPriceType.value = priceType;
    resetPricing();
    validatePrice();
  }
};

const handlePriceUpdate = (updatedPrice: PriceInput) => {
  price.value = updatedPrice;
  validatePrice();
};

watch(
  () => props.shopItemToBeEdited,
  (newShopItem) => {
    if (newShopItem) {
      shopItemName.value = newShopItem.name;
      selectedReward.value = newShopItem.reward;
      selectedTags.value = newShopItem.tagsFormatted;
      price.value.priceType = newShopItem.price?.priceType ?? "IAP";
      selectedPriceType.value = newShopItem.price?.priceType ?? "IAP";
      price.value.IAP.ios.id = newShopItem.price?.IAP?.ios?.id ?? "";
      price.value.IAP.android.id = newShopItem.price?.IAP?.android?.id ?? "";
      price.value.inGameCurrency.itemId = newShopItem.price?.inGameCurrency?.itemId ?? "";
      price.value.inGameCurrency.quantity = newShopItem.price?.inGameCurrency?.quantity ?? 0;
      price.value.RV.quantity = newShopItem.price?.RV?.quantity ?? 1;
      validate();
      validatePrice();
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

onMounted(() => {
  nonConsumableIds.value =
    selectedReward.value?.items.filter((item) => item.type === "NON_CONSUMABLE").map((item) => item.id) || [];
});
</script>

<style module>
.formGroup {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.paddingLeft {
  padding-left: 1rem;
}

.formGroupRow {
  margin-bottom: 1rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}

.label {
  margin-bottom: 0.5rem;
}

.pInvalid {
  border-color: var(--vd-color-error);
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pError {
  color: var(--vd-color-error);
  height: 8px;
}

.pValid {
  color: var(--vd-color-text-light-2);
}

.info {
  margin-top: 0.25rem;
}

.required {
  margin: 1.5rem 0;
  font-size: 12px;
}

.rewardFormGroup {
  margin-bottom: 1.5rem;
}
</style>
